import React from "react";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import { Stack } from "components/layout/reusableLayouts";
import BalanceAmount from "components/common/BalanceAmount";
import TrendChart from "components/common/TrendChart";
import { formatCashFull } from "utils/formatCash";
import useCashFlowSummary from "hooks/useCashFlowSummary";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import type { BankAccount } from "hooks/useGetBankAccounts";

interface BankBalanceCardProps {
  account?: BankAccount;
  /** The account list is still loading, so there is no balance to show yet. */
  loading?: boolean;
  timeFrame?: TimeFrameEnum;
}

/**
 * One account's balance over its curve. A sibling of the dashboard's
 * BalancePanel rather than a reuse of it: this card is always scoped to a
 * single financial account, so the combined-balance, credit-limit and
 * external-card branches that panel carries would all be dead here.
 */
const BankBalanceCard: React.FC<BankBalanceCardProps> = ({
  account,
  loading = false,
  timeFrame = TimeFrameEnum.LAST_30_DAYS,
}) => {
  const { t } = useTranslation();
  const {
    series,
    currency,
    loading: flowLoading,
    // This card draws the curve and nothing else, so the movement sample the
    // hook runs for its in/out totals is a second large query for numbers
    // nobody here reads.
  } = useCashFlowSummary(timeFrame, account?.id, !account?.id, true);

  // A nickname is runtime data, so it never goes through t().
  // Same fallback the picker and the wallet panel use: an account with no
  // nickname still has a bank, and "Account" tells the reader nothing.
  const name = account?.nickname || account?.bankName;
  const title = name ? titleCase(name.toLowerCase()) : t("Account");

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        height: "100%",
        padding: "var(--space-6)",
        gap: "var(--space-4)",
      }}
    >
      <Stack gap="var(--space-2)">
        <Typography variant="h4" component="h3">
          {title}
        </Typography>
        {loading || !account?.availableAmount ? (
          <Typography variant="h2">{"..."}</Typography>
        ) : (
          <BalanceAmount
            value={account.availableAmount}
            variant="h2"
            centsSize="var(--type-size-lg)"
          />
        )}
        <Typography
          variant="label"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("Balance history, last 30 days")}
        </Typography>
      </Stack>

      {/* Skipping the cash-flow query while there is no account makes
          flowLoading false, so without the list's own flag the chart reports
          "No Data Found" during the page's loading state. */}
      <TrendChart
        series={series.map((point) => ({
          date: point.date,
          value: point.balance,
        }))}
        formatValue={(value) => formatCashFull(value, currency)}
        loading={loading || flowLoading}
      />
    </Card>
  );
};

export default BankBalanceCard;
